import { InView } from 'react-intersection-observer';

import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography as Text,
} from '@material-ui/core';

import {
  RiBug2Line,
  RiChat3Line,
  RiLightbulbLine,
  RiPriceTag3Line,
  RiQuestionLine,
  RiThumbDownLine,
} from '@remixicon/react';

import { DateUtils, SearchUtils } from '@spotify/confidence-core-react';
import {
  ObservationRefFragment,
  useObservationsSuspense,
} from '@spotify/plugin-confidence-issues-react';

import styles from './ObservationsList.module.css';

export type ObservationsListProps = {
  issueName?: string;
  onPress?: (observation: ObservationRefFragment) => void;
};

const OBSERVATION_ICON_SIZE = 16;

const getObservationKindIcon = (kind: string) => {
  switch (kind) {
    case 'bug':
      return <RiBug2Line size={OBSERVATION_ICON_SIZE} aria-hidden />;
    case 'complaint':
      return <RiThumbDownLine size={OBSERVATION_ICON_SIZE} aria-hidden />;
    case 'feature_request':
      return <RiLightbulbLine size={OBSERVATION_ICON_SIZE} aria-hidden />;
    case 'open_question':
      return <RiQuestionLine size={OBSERVATION_ICON_SIZE} aria-hidden />;
    case 'pain_point':
      return <RiChat3Line size={OBSERVATION_ICON_SIZE} aria-hidden />;
    default:
      return <RiPriceTag3Line size={OBSERVATION_ICON_SIZE} aria-hidden />;
  }
};

const getObservationDescription = ({
  eventTime,
  source,
}: {
  eventTime: string;
  source: { system: string };
}): string =>
  `From ${source.system} · Observed ${DateUtils.toDateString(
    eventTime,
    DateUtils.MONTH_DAY_FORMAT,
  )}`;

export const ObservationsList = ({
  issueName,
  onPress,
}: ObservationsListProps) => {
  const { observations, nextPage, hasNextPage } = useObservationsSuspense({
    filter: issueName && `issue:${SearchUtils.escapeQuery(issueName)}`,
  });

  return (
    <>
      {observations.length === 0 ? (
        <Text variant="body2" color="textSecondary">
          No observations available.
        </Text>
      ) : (
        <List aria-label="Observations">
          {observations.map(observation => (
            <ListItem
              key={observation.name}
              role="row"
              button
              onClick={() => onPress?.(observation)}
              className={styles.row}
             data-track-source-anchor="ff9d8dbbb7;observations-list-item">
              <ListItemIcon>
                {getObservationKindIcon(observation.kind)}
              </ListItemIcon>
              <ListItemText
                primary={observation.summary}
                secondary={getObservationDescription(observation)}
              />
            </ListItem>
          ))}
        </List>
      )}
      <InView
        onChange={inView => {
          if (inView && hasNextPage) {
            nextPage();
          }
        }}
       data-track-source-anchor="b3dd0d6938;observations-list-in-view"/>
    </>
  );
};
